import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from 'react-query';
import { AxiosError } from 'axios';
import { useCallback } from 'react';

import { queryCreate, queryDelete, queryGet, queryUpdate } from './fetch';
import { Appointment } from './session';
import {
  CompanySize,
  CompanyType,
  Devices,
  EducationLevel,
  FamilyStatus,
  Gender,
  HierarchicalLevel,
  Income,
  Industry,
  ProfessionalSituation,
  TechSavviness,
} from './participantEnums';
import { Account, useCurrentParticipantId } from './auth';
import { PartialNullable } from './types';
import { useTrackJoinedPanel } from '@app/analytics';
import { LocalizedCountry } from './geolocation';

export enum ParticipantSource {
  SESSION = 'SESSION',
  MANUAL_ADDITION = 'MANUAL_ADDITION',
  STANDALONE_FORM = 'STANDALONE_FORM',
}

export enum StudySubjectPreference {
  B2B = 'B2B',
  B2C = 'B2C',
}

export enum LinkedinVerificationStatus {
  VERIFIED = 'VERIFIED',
  NOT_VERIFIED = 'NOT_VERIFIED',
  VERIFICATION_PENDING = 'VERIFICATION_PENDING',
}

export enum StudyTypePreference {
  PHYSICAL = 'PHYSICAL',
  REMOTE = 'REMOTE',
  UNMODERATED = 'UNMODERATED',
}

export interface Participant {
  id: string;
  referralId: string;
  panelId?: string;
  source: ParticipantSource;
  createdAt: Date;
  appointments?: Appointment[];
  tags?: ParticipantTag[];
  sessionId?: string;
  emailValidated: boolean;

  firstName?: string;
  lastName?: string;
  fullName?: string;
  email: string;
  phoneNumber?: string | null;
  phoneValidated?: boolean;
  excluded: boolean;
  linkedinVerificationStatus?: LinkedinVerificationStatus;
  linkedinProfileUrl?: string;

  deletedAt?: Date;
  locales: string[];
  timezone?: string;

  jobTitle?: string;
  city?: string;
  postalCode?: string;
  genderText?: string;
  gender?: Gender | null;
  birthdate?: string;
  devices?: Devices[];

  educationLevel?: EducationLevel;
  familyStatus?: FamilyStatus;
  techSavviness?: TechSavviness;
  hierarchicalLevel?: HierarchicalLevel;
  industries: Industry[];
  companySize?: CompanySize;
  companyType?: CompanyType;
  income?: Income;
  professionalSituation: ProfessionalSituation;

  studySubjectPreferences?: StudySubjectPreference[];
  studyTypePreferences?: StudyTypePreference[];

  evaluationScore?: number;
  activityScore?: number;
  score?: number;

  // fast participants
  lastParticipationDate?: string;
  countryKey?: string;
  countryName?: string;
  tagNames: string;
  tagIds: string;
  rawIndustries: string;
  rawStudySubjectPreferences: string;
  rawStudyTypePreferences: string;
}

export interface ExtendedParticipant extends Participant {
  evaluationScore?: number;
  signupChecksScore?: number;
  invitationCount?: number;
  clickOnInvitationCount?: number;
  applicationCount?: number;
  participationCount?: number;
  activityScore?: number;
  score?: number;

  sessionParticipated?: string[];
  participatedInOrganization?: string[];
  invitedTo?: string[];
  lastInvitation?: Date;
  lastParticipation?: Date;
  lastStillActiveAccepted?: Date;
  hasABookedAppointment: boolean;

  location?: {
    lon: number;
    lat: number;
  };
}

export interface ParticipantTag {
  id: string;
  name: string;
  color: string;
  panelId: string;

  deletedAt?: string;
}

interface RegisterDto {
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  locale?: string;
  countryKey?: string;
  source: ParticipantSource;
  sourceId?: string;
  tracking: {
    utm_source?: string;
    utm_campaign?: string;
    utm_medium?: string;
    utm_term?: string;
    utm_content?: string;
  };
  fingerprint?: string;
  altcha: string;
}

interface UpdateFieldDto {
  fieldName: string;
  text?: string;
  number?: number;
  date?: Date;
  choices?: string[] | number[];
}

export const useRegisterParticipant = (
  options?: UseMutationOptions<{ id: string }, AxiosError, RegisterDto>,
) => {
  const queryClient = useQueryClient();
  return useMutation<Participant, AxiosError, RegisterDto>(
    queryCreate('/participants/register'),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        if (options?.onSuccess) {
          options.onSuccess(data, variables, context);
        }
        queryClient.invalidateQueries(['auth', 'me']);
      },
    },
  );
};

export const useAddToPanel = (
  options?: UseMutationOptions<
    { id: string },
    undefined,
    { participantId: string; panelId: string }
  >,
) => {
  const track = useTrackJoinedPanel();
  return useMutation<
    { id: string },
    undefined,
    { participantId: string; panelId: string; countryKey?: string }
  >(
    ({ participantId, panelId, countryKey }) =>
      queryCreate(`/panels/${panelId}/participants/${participantId}`)({
        countryKey,
      }),
    {
      ...options,
      onSuccess(data, variables, context) {
        track(variables);
        if (options?.onSuccess) {
          options.onSuccess(data, variables, context);
        }
      },
    },
  );
};

export const useUpdateParticipantField = (
  participantId?: string,
  options?: UseMutationOptions<{ id: string }, undefined, UpdateFieldDto>,
) => {
  return useMutation<{ id: string }, undefined, UpdateFieldDto>(
    (data: UpdateFieldDto) => {
      if (!participantId) {
        throw new Error('undefined participantId');
      }
      return queryUpdate(`/participants/${participantId}/field`)(data);
    },
    options,
  );
};

export const useParticipant = (
  id?: string,
  options: UseQueryOptions<Participant> = {},
) => {
  return useQuery<Participant>(
    ['participants', id],
    queryGet(`/participants/${id}`),
    {
      enabled: !!id,
      ...options,
    },
  );
};

export const useParticipationParticipant = (
  { id, participantId }: { id: string; participantId?: string },
  options: UseQueryOptions<Participant> = {},
) => {
  return useQuery<Participant>(
    ['participations', id, 'participant'],
    queryGet(`/participations/${id}/participant`),
    {
      enabled: !!id && !!participantId,
      ...options,
    },
  );
};

export const isParticipantWorker = (participant: Participant) => {
  return (
    participant.professionalSituation === ProfessionalSituation.WORKING ||
    (participant.professionalSituation === ProfessionalSituation.STUDENT &&
      participant.jobTitle)
  );
};

export const useSessionParticipant = (
  sessionId?: string,
  id?: string,
  options: UseQueryOptions<Participant> = {},
) => {
  return useQuery<Participant>(
    ['participants', id],
    queryGet(`/sessions/${sessionId}/participants/${id}`),
    {
      enabled: !!id && !!sessionId,
      ...options,
    },
  );
};

export const useParticipantByIds = (ids?: string[]) => {
  const params = ids && ids.join(',');

  const queryClient = useQueryClient();
  const onSuccess = useCallback(
    (participants: Participant[]) => {
      participants.forEach((p) => {
        queryClient.setQueryData(['participants', p.id], p);
      });
    },
    [queryClient],
  );

  return useQuery<Participant[]>(
    ['participants', params],
    queryGet(`/participants?id=$in::${params}`),
    {
      enabled: !!ids,
      onSuccess,
    },
  );
};

export const useParticipantTags = (
  id?: string,
  options: UseQueryOptions<ParticipantTag[]> = {},
) => {
  return useQuery<ParticipantTag[]>(
    ['participants', id, 'tags'],
    queryGet(`/participants/${id}/tags`),
    {
      enabled: !!id,
      ...options,
    },
  );
};

export const useUpdateParticipantTags = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    Participant,
    Error,
    ParticipantTag[],
    ParticipantTag[] | undefined
  >((tags: ParticipantTag[]) => queryUpdate(`/participants/${id}`)({ tags }), {
    onMutate: (variables: ParticipantTag[]) => {
      queryClient.setQueryData(['participants', id, 'tags'], variables);
      const previous = queryClient.getQueryData<ParticipantTag[]>([
        'participants',
        id,
        'tags',
      ]);
      return previous;
    },
    onError: (
      _error: Error,
      _variables: ParticipantTag[],
      context?: ParticipantTag[],
    ) => {
      queryClient.setQueryData(['participants', id, 'tags'], context);
    },
    onSuccess: (data: Participant) => {
      queryClient.setQueryData(['participants', id, 'tags'], data.tags);
      queryClient.setQueryData(['participants', id], data);
    },
  });
};

export const useDeletePanelParticipants = (panelId: string) => {
  return useMutation<Participant, any, any>((ids: string[]) =>
    queryDelete(`/panels/${panelId}/participants`)({ participantIds: ids }),
  );
};

export const useUpdateParticipant = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<Participant, any, PartialNullable<Participant>>(
    (values: any) => queryUpdate(`/participants/${values.id || id}`)(values),
    {
      onSuccess: (newValue: Participant) => {
        queryClient.setQueriesData(['participants', id], newValue);
      },
    },
  );
};

export const useCurrentParticipant = (allowAnonymous?: boolean) => {
  const participantId = useCurrentParticipantId(allowAnonymous);
  return useParticipant(participantId);
};

export const useGetAccountParticipant = () => {
  const queryClient = useQueryClient();
  return useCallback(
    async (account: Account) => {
      const participantId =
        account.linkedParticipants?.length > 0
          ? account.linkedParticipants[0]
          : undefined;

      if (!participantId) {
        return undefined;
      }
      if (queryClient.getQueryData(['participants', participantId])) {
        return queryClient.getQueryData(['participants', participantId]);
      }
      try {
        const participant = await queryGet(`/participants/${participantId}`)();
        queryClient.setQueriesData(
          ['participants', participantId],
          participant,
        );

        return participant;
      } catch (e) {
        return undefined;
      }
    },
    [queryClient],
  );
};

export const parsePostgresArray = <T = string>(rawValue: string): Array<T> => {
  if (!rawValue) {
    return [];
  }
  if (rawValue === '{}') {
    return [];
  }
  const regRes = /^\{(?<content>([a-zA-Z0-9_]+,?)*)\}$/.exec(rawValue);
  if (!regRes || !regRes.groups || !regRes.groups.content) {
    return [];
  }

  return regRes.groups.content.split(',') as unknown[] as T[];
};

export const useNumberOfPanelParticipants = (id?: string) => {
  return useQuery<{ count: number }>(
    ['panel', id, 'participants-search', 'count'],
    queryGet(`/panels/${id}/participants-search/count`),
    {
      enabled: !!id,
    },
  );
};

interface ParticipantResDto {
  data: Participant[];
  total: number;
  scrollId: string;
}

export const useAllParticipantsInPanel = (id?: string) => {
  const queryClient = useQueryClient();

  return useQuery<Participant[]>(
    ['panel', id, 'participants-search'],
    async ({ signal }) => {
      let promise = queryGet(`/panels/${id}/participants-search`)();
      let stopped = false;

      signal?.addEventListener('abort', () => {
        stopped = true;
        if (promise.cancel) {
          promise.cancel();
        }
      });

      let res = await promise;
      let participants: Participant[] = res.data;
      const total = res.total;
      while (res.data.length && participants.length < total && !stopped) {
        queryClient.setQueryData(['panel', id, 'participants'], participants);
        promise = queryGet(`/panels/${id}/participants-search`, {
          scrollId: res.scrollId,
        })();
        res = await promise;

        participants = participants.concat(res.data);
      }

      return participants;
    },
    {
      enabled: !!id,
      staleTime: 15 * 60 * 1000,
    },
  );
};

export const useFilterParticipantInPanel = (id?: string) => {
  return useMutation<ParticipantResDto, unknown, any>(
    queryCreate(`/panels/${id}/participants-search/search`),
  );
};
